/**
 * RecipientAutocomplete.jsx — typeahead recipient picker for the
 * ComposeModal email `To:` field.
 *
 * Prevents typos when reps send emails from Communications by
 * suggesting matches against:
 *   • Leads (contact_name + email + company)      → labeled "Lead"
 *   • Users / teammates (full_name + email)       → labeled "Team"
 *
 * Behavior:
 *   • Fetches leads (slim projection) + users/directory ONCE the
 *     first time it opens, then filters locally as the rep types.
 *   • Shows a distinct "Use custom address" affordance so the rep
 *     can always send to an arbitrary email that isn't in the CRM.
 *   • Keyboard-nav: ArrowUp / ArrowDown / Enter / Escape.
 *   • Falls back gracefully if either fetch fails (still supports
 *     free-form typing — the send button is never blocked).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { User, Users, Mail } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RecipientAutocomplete({ value, onChange, testid = "compose-to", placeholder = "name@company.com" }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Lazy-fetch directory on first open — avoids paying the cost for
  // reps who type a full known email and never expand the dropdown.
  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    (async () => {
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [leadsRes, usersRes] = await Promise.all([
          axios.get(`${API}/leads`, {
            params: { fields: "slim", limit: 2000, skip: 0 },
            headers,
          }).catch(() => ({ data: [] })),
          axios.get(`${API}/users/directory`, { headers })
            .catch(() => ({ data: { users: [] } })),
        ]);
        if (cancelled) return;
        const leadArr = Array.isArray(leadsRes.data) ? leadsRes.data : [];
        const userArr = Array.isArray(usersRes.data?.users) ? usersRes.data.users : [];
        setLeads(leadArr.filter(l => l && l.email));
        setUsers(userArr.filter(u => u && u.email));
        setLoaded(true);
      } catch {
        setLoaded(true); // don't retry storm; allow free-form
      }
    })();
    return () => { cancelled = true; };
  }, [open, loaded, token]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const suggestions = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    const scored = [];
    const push = (kind, id, name, email, sub) => {
      if (!email) return;
      const hay = `${name || ""} ${email} ${sub || ""}`.toLowerCase();
      if (q && !hay.includes(q)) return;
      scored.push({ kind, id, name: name || email, email: String(email).toLowerCase(), sub });
    };
    for (const u of users) push("user", u.id, u.full_name, u.email, u.role);
    for (const l of leads) push("lead", l.id, l.contact_name, l.email, l.company);
    // De-duplicate by email (a rep who is also a lead should only appear once — user wins).
    const seen = new Set();
    const out = [];
    for (const s of scored) {
      if (seen.has(s.email)) continue;
      seen.add(s.email);
      out.push(s);
      if (out.length >= 8) break;
    }
    return out;
  }, [value, leads, users]);

  // Reset highlight when suggestions change.
  useEffect(() => { setActiveIdx(0); }, [value, open]);

  const q = (value || "").trim();
  const showCustomRow = open && q && EMAIL_RE.test(q) && !suggestions.some(s => s.email === q.toLowerCase());
  const totalRows = suggestions.length + (showCustomRow ? 1 : 0);

  const pick = (email) => {
    onChange?.(email);
    setOpen(false);
    // Keep focus on the input so the rep can Tab to Subject.
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { setOpen(true); e.preventDefault(); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, Math.max(0, totalRows - 1))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Escape") { setOpen(false); }
    else if (e.key === "Enter") {
      if (totalRows === 0) return; // let the parent handle Enter normally
      e.preventDefault();
      if (activeIdx < suggestions.length) pick(suggestions[activeIdx].email);
      else pick(q); // custom row
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        className="input"
        value={value || ""}
        onChange={e => { onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        style={{ marginTop: 3 }}
        data-testid={testid}
        autoComplete="off"
        spellCheck={false}
      />
      {open && (suggestions.length > 0 || showCustomRow || (!loaded && q)) && (
        <div
          data-testid="recipient-autocomplete-menu"
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, marginTop: 3,
            background: "var(--surface)", border: "0.5px solid var(--border)",
            borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
            maxHeight: 260, overflowY: "auto", zIndex: 30,
          }}
        >
          {!loaded && (
            <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text-tertiary)" }}>
              Loading contacts…
            </div>
          )}
          {suggestions.map((s, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={`${s.kind}-${s.id || s.email}`}
                type="button"
                data-testid={`recipient-option-${s.kind}-${s.email}`}
                onMouseDown={e => { e.preventDefault(); pick(s.email); }}
                onMouseEnter={() => setActiveIdx(idx)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "8px 12px", border: "none", cursor: "pointer",
                  background: isActive ? "var(--blue-light, #E6F1FB)" : "transparent",
                  textAlign: "left", fontFamily: "inherit",
                }}
              >
                <span style={{
                  width: 26, height: 26, borderRadius: "50%",
                  background: s.kind === "user" ? "#EEF2FF" : "#F0FDF4",
                  color: s.kind === "user" ? "#4338CA" : "#166534",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {s.kind === "user"
                    ? <Users style={{ width: 13, height: 13 }} />
                    : <User style={{ width: 13, height: 13 }} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary, #111)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {s.name}
                    {s.sub && (
                      <span style={{ fontSize: 10, fontWeight: 400, color: "var(--text-tertiary)", marginLeft: 6 }}>
                        · {s.sub}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {s.email}
                  </div>
                </div>
                <span style={{
                  fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4,
                  padding: "2px 6px", borderRadius: 4,
                  background: s.kind === "user" ? "#EEF2FF" : "#F0FDF4",
                  color: s.kind === "user" ? "#4338CA" : "#166534",
                }}>
                  {s.kind === "user" ? "Team" : "Lead"}
                </span>
              </button>
            );
          })}
          {loaded && suggestions.length === 0 && !showCustomRow && q && (
            <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text-tertiary)" }}>
              No matches in CRM. Type a full email to send to a custom address.
            </div>
          )}
          {showCustomRow && (
            <button
              type="button"
              data-testid="recipient-option-custom"
              onMouseDown={e => { e.preventDefault(); pick(q); }}
              onMouseEnter={() => setActiveIdx(suggestions.length)}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                padding: "8px 12px", border: "none", cursor: "pointer",
                background: activeIdx === suggestions.length ? "var(--blue-light, #E6F1FB)" : "transparent",
                borderTop: suggestions.length > 0 ? "0.5px solid var(--border)" : "none",
                textAlign: "left", fontFamily: "inherit",
              }}
            >
              <span style={{
                width: 26, height: 26, borderRadius: "50%", background: "#FEF3C7", color: "#92400E",
                display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <Mail style={{ width: 13, height: 13 }} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>Send to custom address</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{q}</div>
              </div>
              <span style={{
                fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4,
                padding: "2px 6px", borderRadius: 4, background: "#FEF3C7", color: "#92400E",
              }}>
                Custom
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
